/**
 * atomberg-cart-coupons.js
 *
 * Apply flow for the cart coupon sheet (drawer + cart page): backend coupon
 * cards' Apply buttons and the manually-typed code form, both applying via
 * /cart/update.js so the sheet can stay open until the discount is confirmed
 * (loader on the button, inline error on failure, hydration-morph cart
 * refresh + sheet close on success).
 *
 * Loaded globally via atomberg-checkout-guard.liquid. This used to be an
 * inline <script> in atomberg-cart-coupons.liquid, which renders only on a
 * non-empty cart — on a session that started with an empty cart, add-to-cart
 * morphed the markup into the drawer and the script never executed (morphed-in
 * script tags don't run), so Apply clicks did nothing until a full refresh.
 *
 * Everything is resolved from the clicked element, never a global id. The same
 * sheet id used to live in both the drawer and the cart page, so getElementById
 * resolved the wrong instance and apply/list silently targeted the hidden copy.
 * Deriving context per-click makes any number of coexisting instances (drawer +
 * page) each apply against their own cart body.
 */
(function () {
  'use strict';

  var w = /** @type {any} */ (window);
  if (w._atombergCouponCardInit) return;
  w._atombergCouponCardInit = true;

  /**
   * The sheet body wrapper carrying the section id for this instance.
   * @param {Element} el
   * @returns {HTMLElement|null}
   */
  function scopeFor(el) {
    return /** @type {HTMLElement|null} */ (el.closest('[data-atomberg-coupon-scope]'));
  }

  /**
   * The <div data-atomberg-sheet> ancestor, used only to close the right sheet.
   * @param {Element} el
   * @returns {HTMLElement|null}
   */
  function sheetFor(el) {
    return /** @type {HTMLElement|null} */ (el.closest('[data-atomberg-sheet]'));
  }

  /**
   * The cart body component matching this scope's section id.
   * @param {string} sectionId
   * @returns {HTMLElement|null}
   */
  function componentFor(sectionId) {
    return /** @type {HTMLElement|null} */ (
      document.querySelector(
        'cart-discount-component.atomberg-cart-coupons[data-section-id="' + sectionId + '"]'
      )
    );
  }

  /**
   * Codes already applied, read from the matching body's pills.
   * @param {HTMLElement} comp
   * @returns {string[]}
   */
  function existingCodes(comp) {
    /** @type {string[]} */
    var codes = [];
    comp.querySelectorAll('.cart-discount__pill[data-discount-code]').forEach(function (el) {
      var c = el.getAttribute('data-discount-code');
      if (c) codes.push(c);
    });
    return codes;
  }

  /**
   * @param {HTMLElement} scope
   * @param {string} message
   */
  function setError(scope, message) {
    var err = /** @type {HTMLElement|null} */ (scope.querySelector('[data-atomberg-coupon-error]'));
    if (!err) return;
    err.textContent = message || '';
    err.hidden = !message;
  }

  /**
   * @param {HTMLElement|null} btn
   * @param {boolean} on
   */
  function setLoading(btn, on) {
    if (!btn) return;
    var button = /** @type {HTMLButtonElement} */ (btn);
    if (on) {
      button.setAttribute('data-loading', 'true');
      button.disabled = true;
    } else {
      button.removeAttribute('data-loading');
      button.disabled = false;
    }
  }

  /** @param {HTMLElement|null} sheet */
  function closeSheet(sheet) {
    if (sheet && sheet.id && w.AtombergSheet) w.AtombergSheet.close(sheet.id);
  }

  /**
   * Apply a code through /cart/update.js directly so we can keep the sheet open
   * until the discount is confirmed: a loader on the triggering button, an inline
   * error on failure, and a hydration-morph cart refresh + sheet close on success.
   * @param {string|null} code
   * @param {HTMLElement|null} btn
   */
  function applyCode(code, btn) {
    if (!code || !btn) return;
    var scopeEl = scopeFor(btn);
    var sheet = sheetFor(btn);
    var sectionIdAttr = scopeEl && scopeEl.getAttribute('data-section-id');
    var compEl = sectionIdAttr ? componentFor(sectionIdAttr) : null;
    if (!scopeEl || !sectionIdAttr || !compEl) return;
    var scope = scopeEl;
    var sectionId = sectionIdAttr;
    var appliedCode = code;

    setError(scope, '');
    setLoading(btn, true);

    var codes = existingCodes(compEl);
    if (codes.indexOf(appliedCode) === -1) codes.push(appliedCode);

    var url =
      w.Theme && w.Theme.routes && w.Theme.routes.cart_update_url
        ? w.Theme.routes.cart_update_url
        : '/cart/update.js';

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        discount: codes.join(','),
        sections: [sectionId],
        sections_url: window.location.pathname,
      }),
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (/** @type {any} */ cart) {
        var entry = (cart.discount_codes || []).find(function (/** @type {any} */ d) {
          return d.code === appliedCode;
        });
        // Absent or not applicable → invalid for this cart; keep the sheet open.
        if (!entry || entry.applicable === false) {
          setLoading(btn, false);
          setError(scope, 'Coupon “' + appliedCode + '” is not valid for your cart.');
          return;
        }
        // Applied — refresh the cart body via the hydration-morph path so the
        // open drawer is preserved, then close the sheet.
        if (cart.sections && cart.sections[sectionId] != null) {
          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: { data: { source: 'atomberg-coupon', sections: cart.sections } },
            })
          );
        }
        var manualInput = /** @type {HTMLInputElement|null} */ (
          scope.querySelector('[data-atomberg-coupon-manual] input[name="discount"]')
        );
        if (manualInput) manualInput.value = '';
        setLoading(btn, false);
        closeSheet(sheet);
      })
      .catch(function () {
        setLoading(btn, false);
        setError(scope, 'Something went wrong. Please try again.');
      });
  }

  // Apply a backend coupon card from the sheet.
  document.addEventListener('click', function (e) {
    if (!(e.target instanceof Element)) return;
    var btn = /** @type {HTMLElement|null} */ (e.target.closest('[data-coupon-code]'));
    if (!btn || !scopeFor(btn)) return;
    applyCode(btn.getAttribute('data-coupon-code'), btn);
  });

  // Apply a manually typed code from the sheet.
  document.addEventListener('submit', function (e) {
    if (!(e.target instanceof Element)) return;
    var form = /** @type {HTMLElement|null} */ (e.target.closest('[data-atomberg-coupon-manual]'));
    if (!form || !scopeFor(form)) return;
    e.preventDefault();
    var input = /** @type {HTMLInputElement|null} */ (form.querySelector('input[name="discount"]'));
    var code = input ? input.value.trim() : '';
    if (!code) return;
    applyCode(code, /** @type {HTMLElement|null} */ (form.querySelector('[type="submit"]')));
  });
})();
